import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, ShieldCheck, Lock, CreditCard, Zap } from 'lucide-react';
type PackId = 'ninos' | 'adultos' | 'elite';
interface Pack {
  id: PackId;
  emoji: string;
  name: string;
  description: string;
  originalPrice: number;
  price: number;
  featured?: boolean;
  pills?: {
    label: string;
    type: 'gold' | 'coral';
  }[];
  checkoutUrl: string;
}
const packs: Pack[] = [{
  id: 'ninos',
  emoji: '📘',
  name: 'PACK NIÑOS',
  description: '6 Tomos Niños + 6 Bonus',
  originalPrice: 29990,
  price: 16990,
  checkoutUrl: 'https://profelisto.com.ar/checkouts/cn/hWNCfG3SUdDALaB7ZhnCjrNa/es-ar?_r=AQAB-OuOOotZhBgECO994YzDOSXqakwlHGrGfyNnx09ImDA'
}, {
  id: 'adultos',
  emoji: '📗',
  name: 'PACK ADULTOS & ADOLESCENTES',
  description: '6 Tomos Adultos + 6 Bonus',
  originalPrice: 29990,
  price: 18990,
  checkoutUrl: 'https://profelisto.com.ar/checkouts/cn/hWNCfG3elLMj9zDQUDUR9L3C/es-ar?_r=AQABUl5iW6eI2PJ2VfnAXn8wlezS9px9ryfgs-m1NMDWk5c'
}, {
  id: 'elite',
  emoji: '🏆',
  name: 'MEGAPACK ÉLITE',
  description: '12 Tomos + 6 Bonus + 2 EBOOKS ÉLITE',
  originalPrice: 49990,
  price: 27990,
  featured: true,
  pills: [{
    label: '⭐ MÁS ELEGIDO',
    type: 'gold'
  }, {
    label: 'AHORRÁS $8.000',
    type: 'coral'
  }],
  checkoutUrl: 'https://profelisto.com.ar/checkouts/cn/hWNCfG3rVs02iNZ228JU07l4/es-ar?_r=AQABCcNNU2Fif7U6i7HjQy-6cc0DKEo-16yjaja84hvtKBA'
}];
const formatPrice = (n: number) => '$' + n.toLocaleString('es-AR', {
  minimumFractionDigits: 0
});
// 24-hour countdown timer persisted via localStorage
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  }>({
    hours: 24,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const STORAGE_KEY = 'patin360-offer-expires';
    let expiresAt: number;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && parseInt(stored, 10) > Date.now()) {
        expiresAt = parseInt(stored, 10);
      } else {
        expiresAt = Date.now() + 24 * 60 * 60 * 1000;
        localStorage.setItem(STORAGE_KEY, expiresAt.toString());
      }
    } catch {
      expiresAt = Date.now() + 24 * 60 * 60 * 1000;
    }
    const update = () => {
      const diff = Math.max(0, expiresAt - Date.now());
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(diff % (1000 * 60) / 1000);
      setTimeLeft({
        hours,
        minutes,
        seconds
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="flex items-center justify-center gap-2 md:gap-3 mb-6" data-id="element-192">
      <TimeBox value={pad(timeLeft.hours)} label="HORAS" data-id="element-193" />
      <div className="text-[#D4856A] font-extrabold text-2xl md:text-3xl pb-5" data-id="element-194">
        :
      </div>
      <TimeBox value={pad(timeLeft.minutes)} label="MIN" data-id="element-195" />
      <div className="text-[#D4856A] font-extrabold text-2xl md:text-3xl pb-5" data-id="element-196">
        :
      </div>
      <TimeBox value={pad(timeLeft.seconds)} label="SEG" data-id="element-197" />
    </div>;
};
const TimeBox = ({
  value,
  label
}: {
  value: string;
  label: string;
}) => <div className="flex flex-col items-center" data-id="element-198">
    <div className="bg-brand-950 text-white font-extrabold font-heading text-2xl md:text-4xl rounded-lg w-14 h-14 md:w-20 md:h-20 flex items-center justify-center tabular-nums shadow-md" data-id="element-199">
      {value}
    </div>
    <div className="text-[10px] md:text-xs font-bold text-gray-500 tracking-wider mt-1.5" data-id="element-200">
      {label}
    </div>
  </div>;
interface PricingProps {
  isPrimary?: boolean;
}
export const Pricing = ({
  isPrimary = true
}: PricingProps) => {
  const [selected, setSelected] = useState<PackId>('elite');
  const selectedPack = packs.find(p => p.id === selected) ?? packs[2];
  const ctaLabel = selectedPack.id === 'elite' ? 'EL MEGAPACK ÉLITE' : selectedPack.id === 'ninos' ? 'EL PACK NIÑOS' : 'EL PACK ADULTOS';
  return <section {...isPrimary ? {
    id: 'precios'
  } : {}} className="py-16 md:py-24 bg-[#F5EDE0] px-4" data-id="element-201">
      <div className="max-w-2xl mx-auto" data-id="element-202">
        {/* Urgency banner */}
        <div className="text-center mb-6" data-id="element-203">
          <div className="inline-flex items-center gap-2 bg-[#D4856A]/15 text-[#B86F56] font-bold px-4 py-2 rounded-full text-xs md:text-sm mb-4" data-id="element-204">
            <Clock className="w-4 h-4" data-id="element-205" /> ⏰ PRECIO DE LANZAMIENTO — OFERTA POR
            TIEMPO LIMITADO
          </div>

          <CountdownTimer data-id="element-206" />

          <p className="text-[#B86F56] font-medium text-sm md:text-base" data-id="element-207">
            🔴 El precio sube cuando termina el contador
          </p>
        </div>

        {/* Main checkout card */}
        <div className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-gray-100" data-id="element-208">
          <div className="text-center mb-8 md:mb-10" data-id="element-209">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-brand-950 tracking-tight mb-3" data-id="element-210">
              Elegí tu Pack:
            </h2>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-211">
              Ambas opciones incluyen los 6 Bonus estratégicos
            </p>
          </div>

          {/* Pack radio cards */}
          <div className="space-y-3 md:space-y-4 mb-8" data-id="element-212">
            {packs.map(pack => {
            const isSelected = selected === pack.id;
            return <button key={pack.id} type="button" onClick={() => setSelected(pack.id)} className={`w-full text-left rounded-2xl border-2 transition-all p-4 md:p-5 ${isSelected ? 'border-[#D4856A] bg-[#FBF3EC]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-213">
                  <div className="flex items-start gap-3 md:gap-4" data-id="element-214">
                    <div className="shrink-0 mt-1" data-id="element-215">
                      <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#D4856A]' : 'border-gray-300'}`} data-id="element-216">
                        {isSelected && <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      duration: 0.2
                    }} className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#D4856A]" data-id="element-217" />}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-218">
                      <div className="flex items-start justify-between gap-3" data-id="element-219">
                        <div className="flex-1 min-w-0" data-id="element-220">
                          <div className="flex items-center gap-2 mb-1.5" data-id="element-221">
                            <span className="text-lg md:text-xl" data-id="element-222">
                              {pack.emoji}
                            </span>
                            <h3 className="font-heading text-base md:text-lg font-extrabold text-brand-950 tracking-tight leading-tight" data-id="element-223">
                              {pack.name}
                            </h3>
                          </div>

                          {pack.featured && pack.pills && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-224">
                              {pack.pills.map((pill, i) => <span key={i} className={`inline-block text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full ${pill.type === 'gold' ? 'bg-amber-400 text-amber-950' : 'bg-[#D4856A]/15 text-[#B86F56]'}`} data-id="element-225">
                                  {pill.label}
                                </span>)}
                            </div>}

                          <p className="text-xs md:text-sm text-gray-600 leading-snug" data-id="element-226">
                            {pack.description}
                          </p>
                        </div>

                        <div className="text-right shrink-0" data-id="element-227">
                          <div className="text-gray-400 line-through text-xs md:text-sm leading-tight" data-id="element-228">
                            {formatPrice(pack.originalPrice)}
                          </div>
                          <div className={`font-heading font-extrabold text-xl md:text-2xl leading-tight ${isSelected ? 'text-[#D4856A]' : 'text-brand-950'}`} data-id="element-229">
                            {formatPrice(pack.price)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* CTA */}
          <motion.a href={selectedPack.checkoutUrl} target="_blank" rel="noopener noreferrer" whileHover={{
          scale: 1.01
        }} whileTap={{
          scale: 0.99
        }} className="w-full inline-flex flex-col items-center justify-center bg-[#D4856A] hover:bg-[#C06E52] text-white font-bold text-base md:text-lg py-4 md:py-5 px-6 rounded-2xl shadow-lg transition-colors text-center" data-id="element-230">
            <span data-id="element-231">
              SÍ, QUIERO {ctaLabel} → {formatPrice(selectedPack.price)}
            </span>
          </motion.a>

          {selectedPack.id === 'elite' && <p className="text-center text-xs md:text-sm text-gray-500 italic mt-5 px-4 leading-relaxed" data-id="element-232">
              La opción más elegida por profesores de patín artístico argentinos
            </p>}
        </div>

        {/* Trust signals — 2x2 grid */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-8 max-w-md mx-auto text-sm md:text-base text-gray-700" data-id="element-233">
          <div className="flex items-center gap-2" data-id="element-234">
            <Lock className="w-4 h-4 md:w-5 md:h-5 text-gray-500" data-id="element-235" />
            <span data-id="element-236">Pago seguro</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-237">
            <Zap className="w-4 h-4 md:w-5 md:h-5 text-amber-500" data-id="element-238" />
            <span data-id="element-239">Acceso en 5 min</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-240">
            <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" data-id="element-241" />
            <span data-id="element-242">Garantía 7 días</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-243">
            <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-gray-500" data-id="element-244" />
            <span data-id="element-245">Tarjeta · Transferencia</span>
          </div>
        </div>
      </div>
    </section>;
};